import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const LOG_PREFIX = '[useCarverMutations]';
const BASE = (process.env.REACT_APP_AWP_API_BASE || process.env.REACT_APP_AWP_API_URL || '')
    .replace(/\/$/, '');

const MAX_CONFLICT_ATTEMPTS = 3;
const INITIAL_BACKOFF_MS = 250;

function sleep(delayMs) {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
}

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return {
        'Content-Type': 'application/json',
        ...(token ? {'Authorization': `Bearer ${token}`} : {}),
    };
}

function messageForStatus(status) {
    switch (status) {
        case 401:
            return 'Sign in to change CARVER scores — every change is recorded against a user.';
        case 409:
            return 'Someone else changed this matrix first. Reload and try again.';
        case 422:
            return 'CARVER scoring is not enabled for this entity type.';
        default:
            return 'Failed to save the CARVER score.';
    }
}

/**
 * Writes CARVER factors with the same optimistic-concurrency contract the KB properties use:
 * send the revision we last read, and on a 409 re-read and retry a few times before surfacing
 * the conflict.
 */
export function useCarverMutations({entityId, revision, onMutationSuccess}) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const revisionRef = useRef(revision ?? 0);

    useEffect(() => {
        if (Number.isInteger(revision)) {
            revisionRef.current = Math.max(revisionRef.current, revision);
        }
    }, [revision]);

    useEffect(() => {
        setError(null);
    }, [entityId]);

    const clearError = useCallback(() => setError(null), []);

    const patch = useCallback(async (factors, rationale) => {
        setSaving(true);
        setError(null);
        let attempt = 0;
        let backoffMs = INITIAL_BACKOFF_MS;

        try {
            while (attempt < MAX_CONFLICT_ATTEMPTS) {
                const response = await fetch(
                    `${BASE}/api/entities/${encodeURIComponent(entityId)}/carver`,
                    {
                        method: 'PATCH',
                        headers: authHeaders(),
                        body: JSON.stringify({
                            factors,
                            expectedRevision: revisionRef.current,
                            rationale: rationale || null,
                        }),
                    },
                );

                if (response.ok) {
                    const data = await response.json();
                    revisionRef.current = Number.isInteger(data?.revision) ? data.revision : 0;
                    onMutationSuccess?.(data);
                    return data;
                }

                attempt += 1;
                if (response.status !== 409 || attempt >= MAX_CONFLICT_ATTEMPTS) {
                    const err = new Error(messageForStatus(response.status));
                    err.status = response.status;
                    setError(err);
                    throw err;
                }

                // Re-read the revision we lost the race to, then retry.
                const current = await fetch(
                    `${BASE}/api/entities/${encodeURIComponent(entityId)}/carver`,
                    {headers: authHeaders()},
                ).then((r) => (r.ok ? r.json() : null));
                if (Number.isInteger(current?.revision)) {
                    revisionRef.current = current.revision;
                }
                await sleep(backoffMs);
                backoffMs *= 2;
            }
            throw new Error(messageForStatus(409));
        } catch (err) {
            console.error(`${LOG_PREFIX} Error:`, err);
            if (!err.status) setError(err);
            throw err;
        } finally {
            setSaving(false);
        }
    }, [entityId, onMutationSuccess]);

    const setFactor = useCallback(
        (factor, value, rationale) => patch({[factor]: value}, rationale),
        [patch],
    );

    const clearFactor = useCallback(
        (factor, rationale) => patch({[factor]: null}, rationale),
        [patch],
    );

    return useMemo(
        () => ({setFactor, clearFactor, patch, saving, error, clearError}),
        [setFactor, clearFactor, patch, saving, error, clearError],
    );
}
